import React, { useEffect, useMemo, useState } from "react";
import TableRow from "@mui/material/TableRow";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import Button from "@mui/material/Button";
import { NxtIconButton } from "components/common/nxt-icon-button/nxt-icon-button.component";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ListItemIcon, ListItemText, useTheme } from "@mui/material";

import { PageTitle } from "components/common/Text";
import { getUserList } from "api/PowerUserHelpers";
import { convertCognitoAttr, getCurrentPage, getFilteredListNew, getSortList } from "utils/util-functions";
import CustomTable from "components/table";
import { ActionBar } from "components/common/Wrapper";
import { useAuth } from "hooks/useProvideAuth";
import { TableBodyCell } from "components/table/table.style";
import { deleteUser } from "api/AdminHelpers";
import { showSuccess } from "utils/notifications";
import NewAdminDialog from "components/power-user/NewAdminDialog";
import TableFilterNew from "components/table/TableFilterNew.component";
import useConfirm from "hooks/useConfirm";
import useOrganizations from "hooks/useOrganizations";
import ADMIN_COLUMNS from "./Admins.config";

const AdminsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [isOpenNewAdminModal, setIsOpenNewAdminModal] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [sortOrder, setSortOrder] = useState();
  const [sortBy, setSortBy] = useState();
  const [filterColumn, setFilterColumn] = useState();
  const [filterWord, setFilterWord] = useState();
  const [filterType, setFilterType] = useState();

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const open = Boolean(anchorEl);

  const filteredList = useMemo(
    () => getFilteredListNew(adminList, filterColumn, filterType, filterWord),
    [adminList, filterColumn, filterWord, filterType],
  );
  const totalAdminCount = useMemo(() => filteredList?.length || 0, [filteredList]);

  const sortedList = useMemo(() => {
    const sortColumn = ADMIN_COLUMNS.find(({ id }) => id === sortBy);
    return getSortList(filteredList, sortBy, sortOrder, sortColumn?.type);
  }, [sortBy, sortOrder, filteredList]);

  const paginatedList = useMemo(() => getCurrentPage(sortedList, page, rowsPerPage), [sortedList, page, rowsPerPage]);

  const filterColumns = ADMIN_COLUMNS.filter(({ filter }) => filter);

  const { sub: userId } = useAuth();
  const { orgList: orgListData } = useOrganizations(userId, true);

  const orgList = orgListData.map((g) => ({ value: g.GroupName, label: g.GroupName }));

  const theme = useTheme();
  const [getConfirmation, Confirmation] = useConfirm();

  const adminColumns = useMemo(() => {
    const tempCols = [...ADMIN_COLUMNS];
    tempCols.pop();
    return tempCols;
  }, []);

  const fetchAdmins = async (token) => {
    const body = { username: userId, rolename: "admin", token };

    try {
      const { data } = await getUserList(body);
      const newUsers = (data?.Users ?? []).map((user) => ({
        ...user,
        ...convertCognitoAttr(user.Attributes),
      }));

      if (token) {
        setAdminList((value) => [...value, ...newUsers]);
      } else {
        setAdminList(newUsers);
      }

      if (data.PaginationToken) {
        await fetchAdmins(data.PaginationToken);
      }
    } catch (error) {
      // error handling
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchAdmins();
  }, []);

  const onNewAdmin = () => {
    setIsOpenNewAdminModal(true);
  };

  const onClickMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setCurrentRow(row);
  };

  const onMenuClose = () => {
    setAnchorEl(null);
    setCurrentRow(null);
  };

  const onDeleteAdmin = async () => {
    setAnchorEl(null);

    const isConfirm = await getConfirmation({
      title: "Delete Admin",
      content: "Are you sure that you want to delete the selected admin?",
    });

    if (isConfirm) {
      try {
        const { data } = await deleteUser({
          username: userId,
          userEmail: currentRow?.email,
        });
        showSuccess(data?.message);
        await fetchAdmins();
      } catch (error) {
        // error handling
      }
    }
  };

  const handleRequestSort = (order, orderBy) => {
    setSortOrder(order);
    setSortBy(orderBy);
  };

  const onSearch = (column, columnType, searchContent) => {
    setFilterColumn(column);
    setFilterWord(searchContent);
    setFilterType(columnType);
    setPage(0);
  };

  const onResetFilter = () => {
    setFilterColumn();
    setFilterWord();
    setFilterType();
    setPage(0);
  };

  const onAdminAdd = async () => {
    await fetchAdmins();
  };

  return (
    <>
      <PageTitle>Administrators</PageTitle>

      <ActionBar style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        {adminList?.length > 10 ? (
          <TableFilterNew columns={filterColumns} onSearch={onSearch} onReset={onResetFilter} />
        ) : (
          <div />
        )}

        <NxtIconButton icon={<AddCircleOutlineIcon />} text="New Admin" onClick={onNewAdmin} />
      </ActionBar>

      <CustomTable
        rowsPerPageOptions={[10, 25, 100]}
        totalCount={totalAdminCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(pageNum) => setPage(pageNum)}
        onRowsPerPageChange={(rowPerPage) => setRowsPerPage(rowPerPage)}
      >
        <CustomTable.Header columns={ADMIN_COLUMNS} onRequestSort={handleRequestSort} />

        <CustomTable.Body isLoading={isLoading} deviceData={paginatedList}>
          {paginatedList &&
            paginatedList.map(
              (row) =>
                row && (
                  <TableRow key={row.Username}>
                    {adminColumns.map((column) => {
                      const value = row[column.id];

                      return (
                        <TableBodyCell key={column.id}>{column.format ? column.format(value) : value}</TableBodyCell>
                      );
                    })}
                    <TableBodyCell style={{ width: 30 }}>
                      <Button onClick={(e) => onClickMenu(e, row)} style={{ color: theme.palette.common.black }}>
                        <MoreVertIcon />
                      </Button>
                    </TableBodyCell>
                  </TableRow>
                ),
            )}
        </CustomTable.Body>
      </CustomTable>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={onDeleteAdmin}>
          <ListItemIcon>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {isOpenNewAdminModal && (
        <NewAdminDialog
          open={isOpenNewAdminModal}
          orgList={orgList}
          userId={userId}
          onClose={() => setIsOpenNewAdminModal(false)}
          onAdd={onAdminAdd}
        />
      )}

      <Confirmation />
    </>
  );
};

export default AdminsPage;
